import React, { useEffect, useState } from "react";
import { IMySystemAccessRequestResponse } from "../../interfaces/user/UserInterfaces";
import SystemAccessRequestApi from "../../apis/systemAccessRequests/SystemAccessRequestApi";
import "./SystemAccessRequestStatus.scoped.scss";

interface SystemAccessRequestStatusProps {
  onSubmitNew?: () => void;
}

const SystemAccessRequestStatus: React.FC<SystemAccessRequestStatusProps> = ({ onSubmitNew }) => {
  const [requestData, setRequestData] = useState<IMySystemAccessRequestResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await SystemAccessRequestApi.getMySystemAccessRequest();
        setRequestData(data);
      } catch {
        // Silently fail — user just won't see the status card
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  if (loading || !requestData?.exists) {
    return null;
  }

  const statusClass = requestData.status === "Approved"
    ? "status-approved"
    : requestData.status === "Rejected"
      ? "status-rejected"
      : "status-pending";

  const statusLabel = requestData.status === "New" ? "Pending" : requestData.status;

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit"
    });
  };

  return (
    <div className="access-request-card">
      <h3>System Access Request</h3>
      <div className="status-row">
        <span className="label">Status:</span>
        <span className={`status-badge ${statusClass}`}>{statusLabel}</span>
      </div>
      {requestData.roles && requestData.roles.length > 0 && (
        <div className="detail-row">
          <span className="label">Requested Roles:</span>
          <span className="value">{requestData.roles.join(", ")}</span>
        </div>
      )}
      {requestData.requestReason && (
        <div className="detail-row">
          <span className="label">Your Reason:</span>
          <span className="value">{requestData.requestReason}</span>
        </div>
      )}
      {requestData.createdOn && (
        <div className="detail-row">
          <span className="label">Submitted:</span>
          <span className="value">{formatDate(requestData.createdOn)}</span>
        </div>
      )}
      {requestData.decisionReason && (
        <div className="detail-row">
          <span className="label">Admin Response:</span>
          <span className="value">{requestData.decisionReason}</span>
        </div>
      )}
      {requestData.modifiedOn && requestData.status !== "New" && (
        <div className="detail-row">
          <span className="label">Decision Date:</span>
          <span className="value">{formatDate(requestData.modifiedOn)}</span>
        </div>
      )}
      {requestData.status === "Rejected" && onSubmitNew && (
        <div className="resubmit-row">
          <p className="resubmit-hint">Your request was rejected. You may review the admin's response and submit a new request with corrections.</p>
          <button className="primary-button resubmit-button" onClick={onSubmitNew}>
            Submit New Request
          </button>
        </div>
      )}
      {requestData.status === "Approved" && onSubmitNew && (
        <div className="resubmit-row">
          <p className="resubmit-hint">Your roles may have been changed since this request was approved. You can submit a new access request if you need additional access.</p>
          <button className="primary-button resubmit-button" onClick={onSubmitNew}>
            Submit New Request
          </button>
        </div>
      )}
    </div>
  );
};

export default SystemAccessRequestStatus;
